import { UTCDateMini } from '@date-fns/utc';
import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  differenceInDays,
  differenceInMonths,
  endOfToday,
  format,
  isAfter,
  isToday,
  isValid,
  setDefaultOptions,
  subWeeks,
  subYears,
} from 'date-fns';
import { de } from 'date-fns/locale';

setDefaultOptions({ locale: de });

type NullableDateString = Date | string | null;
type NumericDateInputSegment = 'day' | 'month' | 'year';
export type NumericDateInputFormat = 'dd.mm.yyyy' | 'mm.dd.yyyy';

// Change this value to switch formatted date inputs between dd.mm.yyyy and mm.dd.yyyy.
export const NUMERIC_DATE_INPUT_FORMAT: NumericDateInputFormat = 'dd.mm.yyyy';

const INPUT_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
const GERMAN_DISPLAY_DATE_PATTERN = /^(\d{1,2})\.\s*([A-Za-zÄÖÜäöüß]+)\s+(\d{4})$/;
const ISO_DATE_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}(?:$|T)/;
const NUMERIC_DATE_INPUT_SEPARATOR = '.';
const NUMERIC_DATE_INPUT_SEGMENTS: Record<
  NumericDateInputFormat,
  [NumericDateInputSegment, NumericDateInputSegment, NumericDateInputSegment]
> = {
  'dd.mm.yyyy': ['day', 'month', 'year'],
  'mm.dd.yyyy': ['month', 'day', 'year'],
};
const NUMERIC_DATE_INPUT_SEGMENT_LENGTHS: Record<NumericDateInputSegment, number> = {
  day: 2,
  month: 2,
  year: 4,
};
const GERMAN_MONTH_INDEX_BY_NAME: Record<string, number> = {
  januar: 0,
  februar: 1,
  marz: 2,
  april: 3,
  mai: 4,
  juni: 5,
  juli: 6,
  august: 7,
  september: 8,
  oktober: 9,
  november: 10,
  dezember: 11,
};

export function isInputDateValue(value: string) {
  if (!INPUT_DATE_PATTERN.test(value)) return false;

  const [year, month, day] = value.split('-').map(Number);
  const date = new Date(`${value}T00:00:00`);

  return (
    !Number.isNaN(date.getTime()) &&
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function normalizeGermanMonthName(value: string) {
  return value
    .toLocaleLowerCase('de-CH')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

function toInputDateValue(year: number, month: number, day: number) {
  return [year, month + 1, day].map((part) => part.toString().padStart(2, '0')).join('-');
}

export function getNumericDateInputPlaceholder(format: NumericDateInputFormat = NUMERIC_DATE_INPUT_FORMAT) {
  return format;
}

function isNumericDateInputSegmentInRange(segment: NumericDateInputSegment, value: string) {
  const numericValue = Number(value);
  if (segment === 'day') return numericValue >= 1 && numericValue <= 31;
  if (segment === 'month') return numericValue >= 1 && numericValue <= 12;

  return true;
}

function canStartTwoDigitNumericDateInputSegment(segment: NumericDateInputSegment, value: string) {
  const numericValue = Number(value);
  if (segment === 'day') return numericValue >= 0 && numericValue <= 3;
  if (segment === 'month') return numericValue >= 0 && numericValue <= 1;

  return true;
}

function formatSingleDigitDayMonthShortcut(
  digits: string,
  segments: [NumericDateInputSegment, NumericDateInputSegment, NumericDateInputSegment],
) {
  const [firstDigit, secondDigit] = digits;
  const [firstSegment, secondSegment] = segments;
  const firstSegmentValue = firstDigit.padStart(NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[firstSegment], '0');
  const secondSegmentValue = secondDigit.padStart(NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[secondSegment], '0');

  if (isNumericDateInputSegmentInRange(firstSegment, digits)) return undefined;
  if (!isNumericDateInputSegmentInRange(firstSegment, firstSegmentValue)) return undefined;
  if (!isNumericDateInputSegmentInRange(secondSegment, secondSegmentValue)) return undefined;

  return [firstSegmentValue, secondSegmentValue].join(NUMERIC_DATE_INPUT_SEPARATOR);
}

function formatSingleDigitSecondSegmentShortcut(
  digits: string,
  segments: [NumericDateInputSegment, NumericDateInputSegment, NumericDateInputSegment],
) {
  const firstSegmentLength = NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[segments[0]];
  const firstSegmentValue = digits.slice(0, firstSegmentLength);
  const secondSegmentDigit = digits.slice(firstSegmentLength);
  const secondSegment = segments[1];
  const secondSegmentValue = secondSegmentDigit.padStart(NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[secondSegment], '0');

  if (!isNumericDateInputSegmentInRange(segments[0], firstSegmentValue)) return undefined;
  if (!isNumericDateInputSegmentInRange(secondSegment, secondSegmentValue)) return undefined;
  if (canStartTwoDigitNumericDateInputSegment(secondSegment, secondSegmentDigit)) return undefined;

  return [firstSegmentValue, secondSegmentValue].join(NUMERIC_DATE_INPUT_SEPARATOR);
}

export function formatNumericDateInputDraft(value: string, format: NumericDateInputFormat = NUMERIC_DATE_INPUT_FORMAT) {
  const segments = NUMERIC_DATE_INPUT_SEGMENTS[format];
  const maxLength = segments.reduce((length, segment) => length + NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[segment], 0);
  const rawSegments = value.split(NUMERIC_DATE_INPUT_SEPARATOR);
  if (value.endsWith(NUMERIC_DATE_INPUT_SEPARATOR) && rawSegments.length > 1) {
    const completedSegments = rawSegments.slice(0, -1);
    if (completedSegments.length <= segments.length - 1) {
      const normalizedCompletedSegments = completedSegments.map((segmentValue, index) => {
        const segment = segments[index];
        return segmentValue.replace(/\D/g, '').slice(0, NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[segment]);
      });
      if (normalizedCompletedSegments.every(Boolean)) {
        return normalizedCompletedSegments
          .map((segmentValue, index) => {
            const segment = segments[index];
            return segmentValue.padStart(NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[segment], '0');
          })
          .join(NUMERIC_DATE_INPUT_SEPARATOR)
          .concat(NUMERIC_DATE_INPUT_SEPARATOR);
      }
    }
  }

  const digits = value.replace(/\D/g, '').slice(0, maxLength);
  const singleDigitDayMonthShortcut =
    digits.length === 2 && !value.includes(NUMERIC_DATE_INPUT_SEPARATOR)
      ? formatSingleDigitDayMonthShortcut(digits, segments)
      : undefined;
  if (singleDigitDayMonthShortcut) return singleDigitDayMonthShortcut;

  const singleDigitSecondSegmentShortcut =
    digits.length === NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[segments[0]] + 1 &&
    !value.includes(NUMERIC_DATE_INPUT_SEPARATOR)
      ? formatSingleDigitSecondSegmentShortcut(digits, segments)
      : undefined;
  if (singleDigitSecondSegmentShortcut) return singleDigitSecondSegmentShortcut;

  const separatorPositions = segments.slice(0, -1).reduce<number[]>((positions, segment) => {
    const previousPosition = positions[positions.length - 1] ?? 0;
    return [...positions, previousPosition + NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[segment]];
  }, []);
  let currentIndex = 0;

  const formattedValue = segments
    .map((segment) => {
      const segmentLength = NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[segment];
      const segmentValue = digits.slice(currentIndex, currentIndex + segmentLength);
      currentIndex += segmentLength;
      return segmentValue;
    })
    .filter(Boolean)
    .join(NUMERIC_DATE_INPUT_SEPARATOR);

  return value.endsWith(NUMERIC_DATE_INPUT_SEPARATOR) && separatorPositions.includes(digits.length)
    ? `${formattedValue}${NUMERIC_DATE_INPUT_SEPARATOR}`
    : formattedValue;
}

export function formatNumericDateInput(value: string, format: NumericDateInputFormat = NUMERIC_DATE_INPUT_FORMAT) {
  if (!isInputDateValue(value)) return value;

  const [year, month, day] = value.split('-');
  const segmentValueByName: Record<NumericDateInputSegment, string> = { day, month, year };

  return NUMERIC_DATE_INPUT_SEGMENTS[format]
    .map((segment) => segmentValueByName[segment])
    .join(NUMERIC_DATE_INPUT_SEPARATOR);
}

function isNumericDateInputSegmentCompleteAndValid(segment: NumericDateInputSegment, value: string) {
  if (value.length !== NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[segment]) return true;

  return isNumericDateInputSegmentInRange(segment, value);
}

export function isNumericDateInputDraftValid(
  value: string,
  format: NumericDateInputFormat = NUMERIC_DATE_INPUT_FORMAT,
) {
  const rawSegments = value.split(NUMERIC_DATE_INPUT_SEPARATOR);
  const formatSegments = NUMERIC_DATE_INPUT_SEGMENTS[format];
  if (rawSegments.length > formatSegments.length) return false;

  const segmentsAreValid = rawSegments.every((segmentValue, index) => {
    const segmentName = formatSegments[index];
    return segmentName ? isNumericDateInputSegmentCompleteAndValid(segmentName, segmentValue) : false;
  });
  if (!segmentsAreValid) return false;

  const isCompleteDate =
    rawSegments.length === formatSegments.length &&
    rawSegments.every(
      (segmentValue, index) => segmentValue.length === NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[formatSegments[index]],
    );

  return !isCompleteDate || !!parseNumericDateInput(value, format);
}

export function parseNumericDateInput(value: string, format: NumericDateInputFormat = NUMERIC_DATE_INPUT_FORMAT) {
  const rawSegments = value.trim().split(NUMERIC_DATE_INPUT_SEPARATOR);
  const formatSegments = NUMERIC_DATE_INPUT_SEGMENTS[format];
  if (rawSegments.length !== formatSegments.length) return undefined;

  const segmentValueByName = rawSegments.reduce<Partial<Record<NumericDateInputSegment, string>>>(
    (values, segmentValue, index) => {
      const segmentName = formatSegments[index];
      const expectedLength = NUMERIC_DATE_INPUT_SEGMENT_LENGTHS[segmentName];
      if (!new RegExp(`^\\d{${expectedLength}}$`).test(segmentValue)) return values;

      values[segmentName] = segmentValue;
      return values;
    },
    {},
  );

  const year = Number(segmentValueByName.year);
  const month = Number(segmentValueByName.month);
  const day = Number(segmentValueByName.day);
  if (!year || !month || !day) return undefined;

  const inputDate = toInputDateValue(year, month - 1, day);
  return isInputDateValue(inputDate) ? inputDate : undefined;
}

export function parseGermanDisplayDate(value: string) {
  const match = value.trim().match(GERMAN_DISPLAY_DATE_PATTERN);
  if (!match) return undefined;

  const [, dayValue, monthValue, yearValue] = match;
  const year = Number(yearValue);
  const month = GERMAN_MONTH_INDEX_BY_NAME[normalizeGermanMonthName(monthValue)];
  const day = Number(dayValue);
  if (month === undefined) return undefined;

  const inputDate = toInputDateValue(year, month, day);
  return isInputDateValue(inputDate) ? inputDate : undefined;
}

export function parseDateInputValue(value?: string) {
  if (!value) return undefined;
  return isInputDateValue(value) ? value : (parseNumericDateInput(value) ?? parseGermanDisplayDate(value));
}

export function isDateInputValueOutOfRange(value: string | undefined, min: string, max: string) {
  const dateValue = parseDateInputValue(value);
  return !!dateValue && (dateValue < min || dateValue > max);
}

export const getCurrentDate = () => new UTCDateMini().toISOString().slice(0, 10);
export const getCurrentTime = () => new UTCDateMini().toTimeString().slice(0, 5);

export const formatDateTime = (date?: NullableDateString) => date && format(date, 'dd.MM.yyyy HH:mm');

const LOCAL_DATE_TIME_VALUE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

function toLocalDateFromDateTimeValue(value: string) {
  const match = value.match(LOCAL_DATE_TIME_VALUE_PATTERN);
  if (!match) return undefined;

  const [, year, month, day, hours, minutes] = match;
  return new Date(Number(year), Number(month) - 1, Number(day), Number(hours), Number(minutes));
}

/** Formats a LocalDateTime string (yyyy-MM-ddTHH:mm[:ss]) as "10. Juli 2026 08:05 PM". */
export const formatLocalDateTimeWithGermanMonth = (value?: NullableDateString) => {
  if (!value) return undefined;

  const localDate = typeof value === 'string' ? toLocalDateFromDateTimeValue(value) : new UTCDateMini(value);
  if (!localDate || !isValid(localDate)) return typeof value === 'string' ? formatDateTime(value) : undefined;

  return `${format(localDate, 'dd. MMMM yyyy', { locale: de })} ${format(localDate, 'hh:mm a')}`;
};

export const formatTime = (date?: string | null) => date && format(date, 'HH:mm');
export const formatDate = (date?: NullableDateString) => date && format(date, 'dd.MM.yyyy');
export const formatDateWithGermanMonth = (date?: NullableDateString) => {
  if (!date) return undefined;
  const parsed = new UTCDateMini(date);
  return isValid(parsed) ? format(parsed, 'dd. MMMM yyyy', { locale: de }) : undefined;
};

/** Formats a LocalDateTime string (yyyy-MM-ddTHH:mm[:ss]) as "09. August 2026" without UTC day shift. */
export const formatLocalDateTimeWithGermanMonthDateOnly = (value?: NullableDateString) => {
  if (!value) return undefined;

  const localDate = typeof value === 'string' ? toLocalDateFromDateTimeValue(value) : new UTCDateMini(value);
  if (!localDate || !isValid(localDate))
    return typeof value === 'string' ? formatDateWithGermanMonth(value) : undefined;

  return format(localDate, 'dd. MMMM yyyy', { locale: de });
};
export const formatDateWithGermanMonthOrText = (date?: string | null) =>
  date ? (ISO_DATE_VALUE_PATTERN.test(date.trim()) ? (formatDate(date) ?? date) : date) : '';
// Combines a LocalDate ("yyyy-MM-dd") with an optional LocalTime ("HH:mm[:ss]") for display.
export const formatDateWithOptionalTime = (date: string, time?: string | null) =>
  time ? formatDateTime(`${date}T${time}`) : formatDate(date);
export const formatDateYear = (date?: string | null) => date && format(new UTCDateMini(date), 'yyyy');
export const formatDateForInput = (date: Date | string) => format(date, 'yyyy-MM-dd');
export const formatTimeForInput = (date: Date | string) => format(date, 'HH:mm');

export const isAfterToday = (date?: NullableDateString) => date && isAfter(date, endOfToday());
export const isAfterNow = (date?: NullableDateString) => date && isAfter(date, new UTCDateMini());
export const isTodayOrAfter = (date?: NullableDateString) => date && (isToday(date) || isAfterToday(date));

export const getDifferenceInMonths = (start: string, end: string) => {
  const dateStart = new UTCDateMini(start);
  const dateEnd = new UTCDateMini(end);

  return differenceInMonths(dateEnd, dateStart);
};

export const getDifferenceInDays = (start: string, end: string) => {
  const dateStart = new UTCDateMini(start);
  const dateEnd = new UTCDateMini(end);

  return differenceInDays(dateEnd, dateStart);
};

export const getDifferenceInDaysFromToday = (start: string) => getDifferenceInDays(start, endOfToday().toString());

export const addDateWeeks = (weeks: number, date: Date | string) => addWeeks(new UTCDateMini(date), weeks);
export const subDateWeeks = (weeks: number, date: Date | string) => subWeeks(new UTCDateMini(date), weeks);
export const addDateDays = (days: number, date: Date | string) => addDays(new UTCDateMini(date), days);
export const getDateAfterInputValue = (date?: string | null) =>
  date && isInputDateValue(date) ? formatDateForInput(addDateDays(1, date)) : undefined;

export const addDateMonths = (months: number, date?: string | null) => date && addMonths(new UTCDateMini(date), months);

export const addDateYears = (years: number, date: Date | string) => addYears(new UTCDateMini(date), years);
export const subDateYears = (years: number, date: Date | string) => subYears(new UTCDateMini(date), years);

export const isTimeAfter = (start: string, end: string) => isAfter('2020-01-01T' + end, '2020-01-01T' + start);

export function getDefaultValueForDateInput(currentDate?: string, preselectedDate?: Date | string) {
  if (currentDate) return formatDateForInput(currentDate);
  if (preselectedDate) return formatDateForInput(preselectedDate);
  return '';
}

export function getDefaultValueForTimeInput(currentTime?: string, preselectedTime?: Date | string) {
  if (currentTime) return formatTimeForInput(currentTime);
  if (preselectedTime) return formatTimeForInput(preselectedTime);
  return '';
}

// currentTime is a bare LocalTime string (e.g. "08:30:00") that can't be parsed as a date, so it
// is sliced to "HH:mm" for the time input; preselectedTime is a full date-time and gets formatted.
export function getDefaultValueForLocalTimeInput(currentTime?: string, preselectedTime?: Date | string) {
  if (currentTime) return currentTime.slice(0, 5);
  if (preselectedTime) return formatTimeForInput(preselectedTime);
  return '';
}
